import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getQueue, clearQueue, QUEUE_EVENT } from "@/lib/practiceQueue";
import { Check, ListOrdered, SkipForward, X } from "lucide-react";

const LIST_ENDPOINT = {
  pitman: "/pitman-exercises",
  legal: "/legal-passages",
  typing: "/typing-passages",
};

/**
 * Tab strip shown on practice pages when a queue is active. Every selected
 * exercise becomes a tab — one click jumps straight to it, submitted ones get
 * a green tick. The queue persists until the student exits via the X button.
 */
export default function QueueBar({ module, currentId, hrefFor }) {
  const navigate = useNavigate();
  const [q, setQ] = useState(() => getQueue());
  const [titles, setTitles] = useState({});

  useEffect(() => {
    const sync = () => setQ(getQueue());
    window.addEventListener(QUEUE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(QUEUE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const active = !!q && q.module === module && q.ids.includes(currentId);

  useEffect(() => {
    if (!active) return;
    const ep = LIST_ENDPOINT[module];
    if (!ep) return;
    let cancelled = false;
    api.get(ep).then((r) => {
      if (cancelled) return;
      const map = {};
      (r.data || []).forEach((it) => { if (it && it.id) map[it.id] = it.title; });
      setTitles(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [active, module]);

  if (!active) return null;

  const idx = q.ids.indexOf(currentId);
  const doneSet = new Set(q.done || []);
  const doneCount = q.ids.filter((x) => doneSet.has(x)).length;
  const nextId = idx > -1 && idx < q.ids.length - 1 ? q.ids[idx + 1] : null;

  const exit = () => { clearQueue(); navigate(`/${module}`); };

  return (
    <div className="border-b border-brand-gold/25 bg-gradient-to-r from-brand-gold/10 via-brand-gold/5 to-transparent" data-testid="queue-bar">
      <div className="flex items-center gap-3 px-4 pt-2">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-black text-brand-gold">
          <ListOrdered className="w-3.5 h-3.5" /> Practice queue
        </div>
        <div className="font-mono text-xs font-black text-foreground" data-testid="queue-progress">
          {doneCount}<span className="text-muted-foreground"> / {q.ids.length} done</span>
        </div>
        <div className="flex-1" />
        {nextId ? (
          <button
            type="button"
            onClick={() => navigate(hrefFor(nextId))}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-[#0F172A] text-[11px] uppercase tracking-widest font-black transition-colors"
            data-testid="queue-next-btn"
          >
            Next <SkipForward className="w-3.5 h-3.5" />
          </button>
        ) : (
          <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-brand-emerald text-white text-[11px] uppercase tracking-widest font-black" data-testid="queue-last-badge">
            Last item
          </span>
        )}
        <button
          type="button"
          onClick={exit}
          className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-black/5 text-muted-foreground hover:text-foreground transition-colors"
          title="Exit queue"
          aria-label="Exit queue"
          data-testid="queue-exit-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex items-stretch gap-1 px-4 mt-2 overflow-x-auto" role="tablist" aria-label="Queued exercises" data-testid="queue-tabs">
        {q.ids.map((qid, i) => {
          const isActive = qid === currentId;
          const isDone = doneSet.has(qid);
          const label = titles[qid] || `Exercise ${i + 1}`;
          return (
            <button
              key={qid}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => navigate(hrefFor(qid))}
              data-testid={`queue-tab-${i}`}
              data-state={isActive ? "active" : isDone ? "done" : "pending"}
              title={label}
              className={`flex items-center gap-2 px-3 h-9 rounded-t-lg border-b-2 text-[11px] font-bold whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-card border-brand-gold text-foreground"
                  : isDone
                  ? "bg-brand-emerald/10 border-transparent text-emerald-600 dark:text-emerald-400 hover:bg-brand-emerald/20"
                  : "bg-secondary/60 border-transparent text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              <span className={`font-mono text-[10px] ${isActive ? "text-brand-gold" : "opacity-60"}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="max-w-[140px] truncate">{label}</span>
              {isDone && <Check className="w-3.5 h-3.5 shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
